import React from "react";
import { useTranslation } from "react-i18next";
import { exportJsaPdf } from "../../services/jsaService";
import { jobTypeLabel, riskLabel, RISK_LEVELS } from "../../utils/jsaLabels";

const RISK_COLORS = {
  Low: "#00cc66",
  Medium: "#ffaa00",
  High: "#ff6600",
  Critical: "#ff4466",
};

export default function JsaSummary({
  jsa,
  onStart,
  onEdit,
  onView,
  onHistory,
}) {
  const { t } = useTranslation();
  const cyan = "#00f5ff";
  const border = "rgba(0,245,255,0.15)";

  const highestRisk = jsa?.steps?.reduce((highest, s) => {
    return RISK_LEVELS.indexOf(s.riskLevel) > RISK_LEVELS.indexOf(highest)
      ? s.riskLevel
      : highest;
  }, "");

  const actionButton = {
    flex: 1,
    background: "none",
    border: `1px solid ${border}`,
    color: "rgba(0,245,255,0.6)",
    fontSize: "10px",
    padding: "6px",
    minHeight: "36px",
    borderRadius: "4px",
    cursor: "pointer",
    letterSpacing: "1px",
  };

  return (
    <div
      className="jsa-summary"
      data-testid="jsa-summary"
      style={{
        marginTop: "16px",
        borderTop: `1px solid ${border}`,
        paddingTop: "16px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "8px",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            color: cyan,
            fontSize: "11px",
            fontWeight: "700",
            letterSpacing: "3px",
          }}
        >
          {t("jsa.title")}
        </div>
        {jsa?.locked && (
          <span
            style={{ color: "#ffaa00", fontSize: "10px", letterSpacing: "2px", whiteSpace: "nowrap" }}
          >
            {t("jsa.locked")}
          </span>
        )}
      </div>

      {!jsa ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px" }}>
            {t("jsa.noJsaToday")}
          </span>
          {onStart && (
            <button
              onClick={onStart}
              style={{
                background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                color: "#020817",
                fontWeight: "700",
                fontSize: "10px",
                padding: "6px 16px",
                minHeight: "36px",
                borderRadius: "4px",
                border: "none",
                cursor: "pointer",
                letterSpacing: "1px",
              }}
            >
              {t("jsa.start")}
            </button>
          )}
        </div>
      ) : (
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
              gap: "8px",
              marginBottom: "10px",
            }}
          >
            {[
              { label: t("jsa.jobType"), value: jsa.jobType ? jobTypeLabel(t, jsa.jobType) : t("jsa.na") },
              { label: t("jsa.steps"), value: jsa.steps?.length || 0 },
              {
                label: t("jsa.signatures"),
                value: jsa.signatures?.filter((s) => s.name).length || 0,
              },
              {
                label: t("jsa.maxRisk"),
                value: highestRisk ? riskLabel(t, highestRisk) : t("jsa.na"),
                color: highestRisk
                  ? RISK_COLORS[highestRisk]
                  : "rgba(255,255,255,0.5)",
              },
            ].map((stat) => (
              <div
                key={stat.label}
                style={{
                  backgroundColor: "rgba(0,245,255,0.05)",
                  borderRadius: "6px",
                  padding: "8px",
                }}
              >
                <div
                  style={{
                    color: "rgba(0,245,255,0.4)",
                    fontSize: "8px",
                    letterSpacing: "2px",
                    marginBottom: "2px",
                  }}
                >
                  {stat.label}
                </div>
                <div
                  style={{
                    color: stat.color || "white",
                    fontSize: "14px",
                    fontWeight: "700",
                    overflowWrap: "anywhere",
                  }}
                >
                  {stat.value}
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button onClick={onView} style={actionButton}>
              {t("jsa.view")}
            </button>
            {onEdit && (
              <button
                onClick={onEdit}
                disabled={jsa.locked}
                style={{
                  ...actionButton,
                  color: jsa.locked ? "rgba(255,255,255,0.2)" : "rgba(0,245,255,0.6)",
                  cursor: jsa.locked ? "not-allowed" : "pointer",
                }}
              >
                {t("jsa.edit")}
              </button>
            )}
            <button onClick={onHistory} style={actionButton}>
              {t("jsa.history")}
            </button>
            {jsa._id && (
              <button onClick={() => exportJsaPdf(jsa._id)} style={actionButton}>
                {t("jsa.pdf")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
